<script setup>
import { computed, h, ref, resolveComponent, shallowRef } from 'vue';
import _ from '@/lib/lodash';
import { useElementLang } from '@/helpers/elementLang';

const props = defineProps({
  modelValue: {
    type: [String, Number, Object],
    default() {
      return '';
    },
  },
  label: {
    type: String,
    default() {
      return '';
    },
  },
  placeholder: {
    type: [String, Number],
    default() {
      return '';
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
  required: {
    type: Boolean,
    default() {
      return false;
    },
  },
  error: {
    type: [String, Number, Array],
    default() {
      return '';
    },
  },
  errorClearOnChange: {
    type: Boolean,
    default: true,
  },
  prependIcon: {
    type: String,
    default: '',
  },
  typeWrapper: {
    type: String,
    default() {
      return 'default';
    },
  },
  type: {
    type: String,
    default() {
      return 'date';
    },
  },
  format: {
    type: String,
    default() {
      return 'DD MMMM YYYY';
    },
  },
  help: {
    type: String,
    default() {
      return '';
    },
  },
  defaultTime: {
    type: Object,
  },
  defaultValue: {
    type: Date,
  },
  disabledDate: {
    type: Function,
  },
});
const emit = defineEmits(['update:modelValue', 'change', 'update:error', 'blur']);
const focused = ref(false);
const customPrefix = shallowRef({
  render() {
    return h(
      resolveComponent('ui-icon'),
      {
        size: '14',
        color: '#666',
        name: props.prependIcon,
        lib: 'fa',
        substyle: 'fas',
      },
    );
  },
});

const currentError = computed(() => {
  return _.isArray(props.error) ? props.error[0].message : props.error;
});

const handleVisibleChange = async (value) => {
  emit('update:error', null);
  focused.value = value;
};
const handleFocus = async () => {
  focused.value = true;
};
const handleBLur = async () => {
  focused.value = false;
  emit('blur');
};
const handleChange = async (value) => {
  emit('change', value);
};

const { elementLang } = useElementLang();
</script>

<template>
  <div
    :class="[
      'ui-date-picker',
      `ui-date-picker_${typeWrapper}`,
      { 'ui-date-picker_error': currentError },
      { 'ui-date-picker_disabled': disabled },
      { 'ui-date-picker_prepend': prependIcon!=='' },
      { 'ui-date-picker_focused': focused },
    ]"
  >
    <UiLabel
      v-if="label"
      :label="label"
      :required="required"
      :type="typeWrapper"
    />
    <ElConfigProvider :locale="elementLang">
      <ElDatePicker
        :clearable="false"
        :default-time="defaultTime"
        :default-value="defaultValue"
        :disabled-date="disabledDate"
        :editable="false"
        :format="format"
        :model-value="modelValue"
        :placeholder="placeholder"
        :prefix-icon="customPrefix"
        :type="type"
        class="ui-date-picker__element"
        popper-class="ui-date-picker__popper"
        @blur="handleBLur"
        @change="handleChange"
        @focus="handleFocus"
        @update:model-value="emit('update:modelValue', $event)"
        @visible-change="handleVisibleChange"
      />
    </ElConfigProvider>
    <UiHelp
      v-if="currentError"
      :is-error="true"
      :text="currentError"
      class="ui-date-picker__help"
    />
  </div>
</template>

<style lang="scss">
.el-button.is-plain.el-picker-panel__link-btn{
  border-radius: 8px;
  background: #fff;
  border: 1px solid var(--main-color);
  color: #303634;
  font-weight: 600;
  padding: 7px 15px;
  font-size: 12px;
}
.ui-date-picker {
  font-family: Open Sans, serif;
  width: 100%;

  .el-input__prefix {
    display: none;
  }

  &_prepend {
    .el-input__prefix {
      display: inline-flex;
    }
  }

  &__element {
    height: 100%;

    .el-input__inner {
      color: #000;
      font-family: Open Sans, serif;
      font-size: 12px;
      font-weight: 400;
      line-height: 28px;
    }

    .el-input__inner::placeholder {
      color: rgb(117, 117, 117);
      font-family: Open Sans, serif;
      font-size: 12px;
      font-weight: 400;
    }

    .el-input__wrapper {
      box-shadow: none;
      background-color: transparent;
    }
  }

  &_default & {
    &__element {
      border: 1px solid #d9d9d9;
      border-radius: 8px;
      height: 34px;

      .el-input__wrapper {
        padding: 6px 8px;
      }
    }

  }

  &_registration & {
    &__element {
      border-bottom: 2px solid #ccc;
      border-radius: 0;
      padding: 0;
      height: 45px;

      .el-input__wrapper {
        padding: 11px 11px 11px 1px;
      }

      .el-input__inner {
        font-size: 14px;
      }

      .el-input__inner::placeholder {
        font-size: 14px;
      }
    }

  }

  &_error & {
    &__element {
      border-color: #ff5c70;
    }
  }

  &_disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &_focused & {
    &__element {
      border-color: var(--color-border);
    }
  }

  &__popper {
    .el-date-picker__header-label {
      height: 40px;
      display: inline-flex;
      align-items: center;
      padding-top: 4px;
    }
  }
}
</style>
